import * as Yup from "yup";
import { Helmet } from "react-helmet-async";
// @mui
import { LoadingButton } from "@mui/lab";
import { Container, Grid, Card, Stack, Typography } from "@mui/material";
// components
import { useSettingsContext } from "../components/settings";
import { useSnackbar } from "../components/snackbar";
import FormProvider, { RHFTextField } from "../components/hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import {
    addToPilotGroup,
    isUserInPilotGroup,
    removeFromPilotGroup,
} from "../controllers/user";
import { useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

// ----------------------------------------------------------------------

export default function AddToPilot() {
    const { themeStretch } = useSettingsContext();
    const { enqueueSnackbar } = useSnackbar();

    const defaultValues = {
        userId: "",
    };

    const schema = Yup.object().shape({
        userId: Yup.string().required("User ID is required"),
    });

    const methods = useForm({
        resolver: yupResolver(schema),
        defaultValues,
    });

    const {
        reset,
        handleSubmit,
        formState: { isSubmitting, isValid },
    } = methods;

    const [isInPilotGroup, setIsInPilotGroup] = useState<boolean | null>(null);
    const [isCheckingPilotStatus, setIsCheckingPilotStatus] = useState(false);

    const debounceCheckUserPilotGroup = useDebouncedCallback(
        async (userId?: string) => {
            if (!userId || userId.length === 0) {
                setIsInPilotGroup(null);
                setIsCheckingPilotStatus(false);
                return;
            }

            setIsCheckingPilotStatus(true);
            try {
                const inPilotGroup = await isUserInPilotGroup(userId);
                setIsInPilotGroup(inPilotGroup);
            } catch (error) {
                console.error("Error checking pilot status:", error);
                setIsInPilotGroup(null);
            } finally {
                setIsCheckingPilotStatus(false);
            }
        },
        300
    );

    useEffect(() => {
        const subscription = methods.watch((value) => {
            debounceCheckUserPilotGroup(value.userId);
        });
        return () => subscription.unsubscribe();
    }, [methods, debounceCheckUserPilotGroup]);

    const onSubmit = async (data: { userId: string }) => {
        // Determine action based on current pilot status
        const action = isInPilotGroup ? "remove" : "add";

        if (action === "add") {
            addToPilotGroup(data.userId)
                .then(() => {
                    reset();
                    setIsInPilotGroup(null); // Reset status after successful action
                    enqueueSnackbar(
                        `Successfully added user to pilot group: ${data.userId}`
                    );
                })
                .catch((error) => {
                    console.error(error);
                    enqueueSnackbar(
                        `Could not add user to pilot group: ${data.userId}`,
                        {
                            variant: "error",
                        }
                    );
                });
        } else if (action === "remove") {
            removeFromPilotGroup(data.userId)
                .then(() => {
                    reset();
                    setIsInPilotGroup(null); // Reset status after successful action
                    enqueueSnackbar(
                        `Successfully removed user from pilot group: ${data.userId}`
                    );
                })
                .catch((error) => {
                    console.error(error);
                    enqueueSnackbar(
                        `Could not remove user from pilot group: ${data.userId}`,
                        {
                            variant: "error",
                        }
                    );
                });
        }
    };

    return (
        <>
            <Helmet>
                <title> Pilot Group | Admin Portal</title>
            </Helmet>

            <Container maxWidth={themeStretch ? false : "xl"}>
                <Typography variant="h3" component="h1" paragraph>
                    Pilot Group
                </Typography>

                <FormProvider
                    methods={methods}
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={8}>
                            <Card sx={{ p: 3 }}>
                                <Stack spacing={3}>
                                    <RHFTextField
                                        name="userId"
                                        label="User ID"
                                        required
                                    />
                                </Stack>
                                <Stack sx={{ mt: 2 }}>
                                    {isCheckingPilotStatus && (
                                        <Typography
                                            variant="subtitle2"
                                            color={"text.secondary"}
                                        >
                                            Checking pilot status...
                                        </Typography>
                                    )}
                                    {!isCheckingPilotStatus && isInPilotGroup === true && (
                                        <Typography
                                            variant="subtitle2"
                                            color={"success.main"}
                                        >
                                            ✓ This user is in the pilot group.
                                        </Typography>
                                    )}
                                    {!isCheckingPilotStatus && isInPilotGroup === false && (
                                        <Typography
                                            variant="subtitle2"
                                            color={"text.secondary"}
                                        >
                                            ✗ This user is not in the pilot group.
                                        </Typography>
                                    )}
                                </Stack>
                            </Card>
                            <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
                                {!isCheckingPilotStatus && isInPilotGroup === true ? (
                                    <LoadingButton
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        color="error"
                                        size="large"
                                        loading={isSubmitting}
                                        disabled={!isValid}
                                    >
                                        Remove From Pilot Group
                                    </LoadingButton>
                                ) : !isCheckingPilotStatus && isInPilotGroup === false ? (
                                    <LoadingButton
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        color="success"
                                        size="large"
                                        loading={isSubmitting}
                                        disabled={!isValid}
                                    >
                                        Add To Pilot Group
                                    </LoadingButton>
                                ) : (
                                    <LoadingButton
                                        fullWidth
                                        type="submit"
                                        variant="outlined"
                                        size="large"
                                        loading={isCheckingPilotStatus}
                                        disabled={true}
                                    >
                                        {isCheckingPilotStatus
                                            ? "Checking Pilot Status..."
                                            : "Enter a User ID to Continue"}
                                    </LoadingButton>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>
                </FormProvider>
            </Container>
        </>
    );
}
